import { Drn, DrnSchema } from '@duke-hq/drn';
import { z } from 'zod';

export const MAX_CONCURRENT_UPLOADS = 5;
export const UPLOAD_POLL_INTERVAL_MS = 1000;
// TODO: UPLOAD_CHUNK_SIZE_MB should be determined by the size of the file
export const UPLOAD_CHUNK_SIZE_MB = 5 * 1024 * 1024; // 5MB
export const MAX_FAILED_ATTEMPTS = 3;
export const MAX_FILE_UPLOAD_SIZE_MB = 1024; // 1GB

export const AcceptableFileTypeSchema = z.record(z.string(), z.array(z.string()));

export type AcceptableFilesType = z.infer<typeof AcceptableFileTypeSchema>;

export const ACCEPTABLE_IMAGES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/bmp': ['.bmp'],
  'image/webp': ['.webp'],
} satisfies AcceptableFilesType;

export const ACCEPTABLE_VIDEOS = {
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/x-msvideo': ['.avi'],
} satisfies AcceptableFilesType;

export const ACCEPTABLE_DOCUMENTS = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc', '.docx'],
  'application/vnd.ms-excel': ['.xls', '.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt', '.pptx'],
} satisfies AcceptableFilesType;

export const ACCEPTABLE_AUDIO = {
  'audio/mpeg': ['.mp3'],
  'audio/wav': ['.wav'],
  'audio/ogg': ['.ogg'],
  'audio/flac': ['.flac'],
  'audio/aac': ['.aac'],
  'audio/webm': ['.weba'],
  'audio/mp4': ['.m4a'],
  'audio/x-ms-wma': ['.wma'],
} satisfies AcceptableFilesType;

export const ACCEPTABLE_FILES = {
  ...ACCEPTABLE_IMAGES,
  ...ACCEPTABLE_VIDEOS,
  ...ACCEPTABLE_DOCUMENTS,
  ...ACCEPTABLE_AUDIO,
} satisfies AcceptableFilesType;

export type FileUploadStatus = 'pending' | 'confirmingUpload' | 'complete' | 'failed' | 'aborted';

export type FileUpload = {
  id: string;
  name: string;
  previewUrl?: string | undefined;
  progress: number;
  sizeInBytes: number;
  type: string;
  remove: () => void;
  status: FileUploadStatus;
  failed: boolean;
};

export type FileUploadPart = {
  partNumber: number;
  progress?: number;
  uploadUrl?: string;
  checksumSHA256?: string | undefined;
  // AWS returns an EntityTag (ETag) on successful upload of a part, and we
  // need to send this back to AWS to complete the upload.
  etag?: string | undefined;
};

export type PendingFile = {
  id: string;
  name: string;
  uploadId: string;
  previewUrl?: string | undefined;
  drn: Drn;
  sizeInBytes: number;
  mimetype: string;
  parts: FileUploadPart[];
  file: File;
  status: FileUploadStatus;
  failedAttempts: number;
  attachedToKind?: string;
  attachedToId?: string;
};

// -- API Schemas --------------------------------------------------------------

export const UploadFileRequestSchema = z.object({
  name: z.string(),
  mimetype: z.string(),
  // Optional file size in bytes from https://w3c.github.io/FileAPI/#dfn-size
  sizeInBytes: z.number(),
  attachedToKind: z.string().optional().nullable(),
  attachedToId: z.string().optional().nullable(),
});

export type UploadFileRequest = z.infer<typeof UploadFileRequestSchema>;

export const UploadFileDataSchema = z.object({
  // A unique identifier for this file
  id: z.string().cuid2(),
  // the file this upload is for
  name: z.string(),
  // id to be passed back when asking for an upload url per part
  uploadId: z.string(),
  // A file DRN that you can use with `createSignedDownloadUrl()` to get
  // a download link.
  drn: DrnSchema,
  sizeInBytes: z.number(),
  mimetype: z.string(),
  attachedToKind: z.string().optional().nullable(),
  attachedToId: z.string().optional().nullable(),
});

export type UploadFileData = z.infer<typeof UploadFileDataSchema>;

export const FileVisibilitySchema = z.enum(['public', 'private', 'sensitive']).default('private');
export type FileVisibility = z.infer<typeof FileVisibilitySchema>;

export const CreateFilesInputSchema = z.object({
  attachedToKind: z.string().min(1).max(255).optional().nullable(),
  attachedToId: z.string().min(1).max(255).optional().nullable(),
  visibility: FileVisibilitySchema.optional(),
  uploadFiles: z.array(UploadFileRequestSchema),
});

export type CreateFilesInput = z.infer<typeof CreateFilesInputSchema>;

export const CreateFilesOutputSchema = z.object({
  files: z.array(UploadFileDataSchema),
});

export type CreateFilesOutput = z.infer<typeof CreateFilesOutputSchema>;

export const CreatePartUploadUrlInputSchema = z.object({
  fileDrn: DrnSchema,
  uploadId: z.string(),
  partNumber: z.number(),
  checksumSHA256: z.string(),
});

export type CreatePartUploadUrlInput = z.infer<typeof CreatePartUploadUrlInputSchema>;

export const CreatePartUploadUrlOutputSchema = z.url();

export type CreatePartUploadUrlOutput = z.infer<typeof CreatePartUploadUrlOutputSchema>;

export const CompleteFileUploadPart = z.object({
  partNumber: z.number(),
  checksumSHA256: z.string(),
  etag: z.string(),
});

export const CompleteFileUploadInputSchema = z.object({
  fileDrn: DrnSchema,
  uploadId: z.string(),
  parts: z.array(CompleteFileUploadPart),
});

export type CompleteFileUploadInput = z.infer<typeof CompleteFileUploadInputSchema>;

export const CompleteFileUploadOutputSchema = z.object({
  drn: DrnSchema,
  uploadId: z.string(),
});

export type CompleteFileUploadOutput = z.infer<typeof CompleteFileUploadOutputSchema>;

export const CancelFileUploadInputSchema = z.object({
  fileDrn: DrnSchema,
  uploadId: z.string(),
});

export type CancelFileUploadInput = z.infer<typeof CancelFileUploadInputSchema>;

export const CancelFileUploadOutputSchema = z.object({
  drn: DrnSchema,
});

export type CancelFileUploadOutput = z.infer<typeof CancelFileUploadOutputSchema>;

export const DeleteFileUploadInputSchema = z.object({
  fileDrn: DrnSchema,
  uploadId: z.string(),
});

export type DeleteFileUploadInput = z.infer<typeof DeleteFileUploadInputSchema>;

export const DeleteFileUploadOutputSchema = z.object({
  drn: DrnSchema,
});

export type DeleteFileUploadOutput = z.infer<typeof DeleteFileUploadOutputSchema>;
